import React, { useState, useEffect } from 'react';
import api from '../api/client';

interface InactiveUser {
  _id: string;
  name: string;
  email: string;
  createdAt: string;
  lastLogin?: string | null;
  lastEmailSentAt?: string | null;
  lastEmailTemplateName?: string | null;
}

interface Template {
  _id: string;
  name: string;
  subject: string;
  html: string;
}

type SortKey = 'createdAt' | 'lastLogin' | 'lastEmailSentAt';
type SortOrder = 'asc' | 'desc';

const styles: Record<string, React.CSSProperties> = {
  container: { padding: '24px', fontFamily: '"Inter", sans-serif' },
  heading: { fontSize: '24px', marginBottom: '16px', color: '#1a1a1a' },
  tabs: { display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' },
  tabButton: {
    padding: '10px 18px',
    borderRadius: '16px',
    background: 'linear-gradient(135deg, rgba(255,255,255,0.7) 0%, rgba(240,245,255,0.5) 100%)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    border: '1px solid rgba(255,255,255,0.6)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.8)',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 600,
    transition: 'all 0.25s ease',
    letterSpacing: '0.3px',
    color: '#333',
    textShadow: 'none'
  },
  tabButtonActive: {
    background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
    color: '#fff',
    border: '1px solid rgba(255,255,255,0.6)',
    boxShadow: '0 8px 24px rgba(37,117,252,0.4), inset 0 1px 0 rgba(255,255,255,0.6)',
    textShadow: '0 1px 2px rgba(0,0,0,0.2)',
    transform: 'translateY(-2px)'
  },
  filterRow: { display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' },
  select: { padding: '10px 14px', borderRadius: '8px', border: '1px solid #ccc', fontSize: '14px', minWidth: '200px' },
  button: {
    padding: '10px 18px',
    borderRadius: '8px',
    border: 'none',
    background: '#2575fc',
    color: '#fff',
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: '14px'
  },
  secondaryButton: {
    padding: '10px 18px',
    borderRadius: '8px',
    border: '1px solid #ccc',
    background: '#fff',
    color: '#333',
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: '14px'
  },
  dangerButton: {
    padding: '8px 14px',
    borderRadius: '8px',
    border: '1px solid #ffcccc',
    background: '#fff0f0',
    color: '#cc0000',
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: '13px'
  },
  alertSuccess: { padding: '12px 16px', background: '#e6ffed', color: '#1e7a34', borderRadius: '8px', marginBottom: '12px' },
  alertError: { padding: '12px 16px', background: '#ffecec', color: '#b42318', borderRadius: '8px', marginBottom: '12px' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid #e0e0e0',
    color: '#555', fontSize: '13px', cursor: 'pointer', userSelect: 'none',
    transition: 'color 0.2s'
  },
  td: { padding: '10px 12px', borderBottom: '1px solid #f0f0f0', fontSize: '14px', whiteSpace: 'nowrap' },
  checkbox: { width: '18px', height: '18px', cursor: 'pointer' },
  modalBackdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modalContent: {
    background: '#fff',
    borderRadius: '12px',
    padding: '24px',
    width: '90%',
    maxWidth: '800px',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
  },
  formGroup: { marginBottom: '16px' },
  label: { display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '14px', color: '#444' },
  input: { width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ccc', fontSize: '14px' },
  textarea: { width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ccc', fontSize: '14px', minHeight: '200px', fontFamily: 'monospace' },
  templateActions: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  filterLabel: { fontSize: '13px', color: '#555', marginRight: '4px' },
  dateInput: { padding: '8px 10px', borderRadius: '8px', border: '1px solid #ccc', fontSize: '13px' },
  sortIndicator: { marginLeft: '4px', fontSize: '12px', color: '#2575fc' }
};

const InactiveUsersEmail: React.FC = () => {
  const [period, setPeriod] = useState<'2days' | 'week' | 'month'>('2days');
  const [users, setUsers] = useState<InactiveUser[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ message: string } | null>(null);
  const [error, setError] = useState('');

  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');
  const [lastLoginFrom, setLastLoginFrom] = useState('');
  const [lastLoginTo, setLastLoginTo] = useState('');

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewSubject, setPreviewSubject] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [isSending, setIsSending] = useState(false);

  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [templateForm, setTemplateForm] = useState({ name: '', subject: '', html: '' });

  const [showTemplatesList, setShowTemplatesList] = useState(false);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTemplateId, setDeleteTemplateId] = useState<string>('');
  const [deleteTemplateName, setDeleteTemplateName] = useState('');

  // Сортировка
  const [sortKey, setSortKey] = useState<SortKey>('createdAt');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  useEffect(() => {
    fetchUsers();
  }, [period, createdFrom, createdTo, lastLoginFrom, lastLoginTo]);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const params: any = { period };
      if (createdFrom) params.createdFrom = createdFrom;
      if (createdTo) params.createdTo = createdTo;
      if (lastLoginFrom) params.lastLoginFrom = lastLoginFrom;
      if (lastLoginTo) params.lastLoginTo = lastLoginTo;
      const res = await api.get('/admin/email/inactive-users', { params });
      setUsers(res.data);
      setSelectedUsers([]);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Ошибка загрузки пользователей');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await api.get('/admin/email/templates');
      setTemplates(res.data);
      if (res.data.length > 0) setSelectedTemplate(res.data[0]._id);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Ошибка загрузки шаблонов');
    }
  };

  // Обработчик клика по заголовку для сортировки
  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortOrder('asc');
    }
  };

  // Сортировка пользователей
  const sortedUsers = [...users].sort((a, b) => {
    const aVal = a[sortKey];
    const bVal = b[sortKey];
    if (aVal === null || aVal === undefined) return sortOrder === 'asc' ? -1 : 1;
    if (bVal === null || bVal === undefined) return sortOrder === 'asc' ? 1 : -1;
    const aTime = new Date(aVal).getTime();
    const bTime = new Date(bVal).getTime();
    if (aTime === bTime) return 0;
    return sortOrder === 'asc' ? aTime - bTime : bTime - aTime;
  });

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) setSelectedUsers(sortedUsers.map(u => u._id));
    else setSelectedUsers([]);
  };

  const handleSelectUser = (id: string) => {
    setSelectedUsers(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleSend = async (customSubject?: string, customHtml?: string) => {
    if (!selectedTemplate || selectedUsers.length === 0) {
      setError('Выберите шаблон и хотя бы одного пользователя');
      return;
    }
    setIsSending(true);
    setError('');
    try {
      const res = await api.post('/admin/email/send', {
        templateId: selectedTemplate,
        userIds: selectedUsers,
        subject: customSubject,
        html: customHtml
      });
      setResult(res.data);
      setSelectedUsers([]);
      setPreviewOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Ошибка отправки');
    } finally {
      setIsSending(false);
    }
  };

  const openPreview = () => {
    const template = templates.find(t => t._id === selectedTemplate);
    if (!template) {
      setError('Выберите шаблон');
      return;
    }
    const firstUser = sortedUsers.find(u => selectedUsers.includes(u._id));
    const name = firstUser?.name || 'Имя';
    const email = firstUser?.email || 'email@example.com';
    setPreviewSubject(template.subject.replace(/\{\{name\}\}/g, name).replace(/\{\{email\}\}/g, email));
    setPreviewHtml(template.html.replace(/\{\{name\}\}/g, name).replace(/\{\{email\}\}/g, email));
    setPreviewOpen(true);
  };

  const openCreateTemplate = () => {
    setEditingTemplate(null);
    setTemplateForm({ name: '', subject: '', html: '' });
    setShowTemplateForm(true);
  };

  const openEditTemplate = (template: Template) => {
    setEditingTemplate(template);
    setTemplateForm({ name: template.name, subject: template.subject, html: template.html });
    setShowTemplateForm(true);
  };

  const saveTemplate = async () => {
    try {
      if (editingTemplate) {
        const res = await api.put(`/admin/email/templates/${editingTemplate._id}`, templateForm);
        setTemplates(prev => prev.map(t => t._id === editingTemplate._id ? res.data : t));
      } else {
        const res = await api.post('/admin/email/templates', templateForm);
        setTemplates(prev => [...prev, res.data]);
        if (!selectedTemplate) setSelectedTemplate(res.data._id);
      }
      setShowTemplateForm(false);
      setTemplateForm({ name: '', subject: '', html: '' });
      setEditingTemplate(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Ошибка сохранения шаблона');
    }
  };

  const confirmDeleteTemplate = (template: Template) => {
    setDeleteTemplateId(template._id);
    setDeleteTemplateName(template.name);
    setShowDeleteConfirm(true);
  };

  const deleteTemplate = async () => {
    try {
      await api.delete(`/admin/email/templates/${deleteTemplateId}`);
      setTemplates(prev => prev.filter(t => t._id !== deleteTemplateId));
      if (selectedTemplate === deleteTemplateId) {
        const remaining = templates.filter(t => t._id !== deleteTemplateId);
        if (remaining.length > 0) setSelectedTemplate(remaining[0]._id);
        else setSelectedTemplate('');
      }
      setShowDeleteConfirm(false);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Ошибка удаления шаблона');
      setShowDeleteConfirm(false);
    }
  };

  const tabStyle = (val: '2days' | 'week' | 'month') => ({
    ...styles.tabButton,
    ...(period === val ? styles.tabButtonActive : {})
  });

  return (
    <div style={styles.container}>
      <h4 style={styles.heading}>Неактивные пользователи</h4>

      <div style={styles.tabs}>
        <button style={tabStyle('2days')} onClick={() => setPeriod('2days')}>2 дня</button>
        <button style={tabStyle('week')} onClick={() => setPeriod('week')}>Неделя</button>
        <button style={tabStyle('month')} onClick={() => setPeriod('month')}>Месяц</button>
        <button
          style={styles.secondaryButton}
          onClick={() => setShowTemplatesList(prev => !prev)}
        >
          {showTemplatesList ? 'Скрыть шаблоны' : 'Шаблоны рассылок'}
        </button>
        <button style={styles.secondaryButton} onClick={openCreateTemplate}>Создать шаблон</button>
      </div>

      {showTemplatesList && (
        <div style={{ marginTop: '32px' }}>
          <h5 style={{ fontSize: '18px', marginBottom: '12px' }}>Шаблоны рассылок</h5>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Название</th>
                <th style={styles.th}>Тема</th>
                <th style={styles.th}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {templates.map(template => (
                <tr key={template._id}>
                  <td style={styles.td}>{template.name}</td>
                  <td style={styles.td}>{template.subject}</td>
                  <td style={styles.td}>
                    <div style={styles.templateActions}>
                      <button style={styles.secondaryButton} onClick={() => openEditTemplate(template)}>Редактировать</button>
                      <button style={styles.dangerButton} onClick={() => confirmDeleteTemplate(template)}>Удалить</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}


<div style={styles.filterRow}>
        <label style={styles.filterLabel}>Дата регистрации с:</label>
        <input type="date" style={styles.dateInput} value={createdFrom} onChange={e => setCreatedFrom(e.target.value)} />
        <label style={styles.filterLabel}>по:</label>
        <input type="date" style={styles.dateInput} value={createdTo} onChange={e => setCreatedTo(e.target.value)} />

        <span style={{ marginLeft: '16px' }}></span>

        <label style={styles.filterLabel}>Последний вход с:</label>
        <input type="date" style={styles.dateInput} value={lastLoginFrom} onChange={e => setLastLoginFrom(e.target.value)} />
        <label style={styles.filterLabel}>по:</label>
        <input type="date" style={styles.dateInput} value={lastLoginTo} onChange={e => setLastLoginTo(e.target.value)} />
      </div>

      {error && <div style={styles.alertError}>{error}</div>}
      {result && <div style={styles.alertSuccess}>{result.message}</div>}

      <div style={styles.filterRow}>
        <select
          style={styles.select}
          value={selectedTemplate}
          onChange={(e) => setSelectedTemplate(e.target.value)}
        >
          {templates.map(t => (
            <option key={t._id} value={t._id}>{t.name}</option>
          ))}
        </select>
        <button style={styles.secondaryButton} onClick={openPreview} disabled={selectedUsers.length === 0}>Предпросмотр</button>
        <button style={styles.button} onClick={() => handleSend()} disabled={isSending || selectedUsers.length === 0}>
          {isSending ? 'Отправка...' : 'Отправить выбранным'}
        </button>
      </div>

      {loading ? (
        <div>Загрузка...</div>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>
                <input
                  type="checkbox"
                  style={styles.checkbox}
                  onChange={handleSelectAll}
                  checked={selectedUsers.length === sortedUsers.length && sortedUsers.length > 0}
                  ref={el => {
                    if (el) el.indeterminate = selectedUsers.length > 0 && selectedUsers.length < sortedUsers.length;
                  }}
                />
              </th>
              <th style={styles.th}>Имя</th>
              <th style={styles.th}>Email</th>
              <th style={styles.th} onClick={() => handleSort('createdAt')} title="Сортировать по дате регистрации">
                Дата регистрации
                {sortKey === 'createdAt' && (
                  <span style={styles.sortIndicator}>{sortOrder === 'asc' ? '▲' : '▼'}</span>
                )}
              </th>
              <th style={styles.th} onClick={() => handleSort('lastLogin')} title="Сортировать по последнему входу">
                Последний вход
                {sortKey === 'lastLogin' && (
                  <span style={styles.sortIndicator}>{sortOrder === 'asc' ? '▲' : '▼'}</span>
                )}
              </th>
                <th
                  style={styles.th}
                  onClick={() => handleSort('lastEmailSentAt')}
                  title="Сортировать по дате отправки"
                >
                  Дата отправки
                  {sortKey === 'lastEmailSentAt' && (
                    <span style={styles.sortIndicator}>{sortOrder === 'asc' ? '▲' : '▼'}</span>
                  )}
                </th>
                <th style={styles.th}>Шаблон</th>
            </tr>
          </thead>
          <tbody>
            {sortedUsers.map(user => (
              <tr key={user._id}>
                <td style={styles.td}>
                  <input
                    type="checkbox"
                    style={styles.checkbox}
                    checked={selectedUsers.includes(user._id)}
                    onChange={() => handleSelectUser(user._id)}
                  />
                </td>
                <td style={styles.td}>{user.name}</td>
                <td style={styles.td}>{user.email}</td>
                <td style={styles.td}>{user.createdAt ? new Date(user.createdAt).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                <td style={styles.td}>
                  {user.lastLogin
                    ? new Date(user.lastLogin).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                    : 'Никогда'}
                </td>
                <td style={styles.td}>
                  {user.lastEmailSentAt
                    ? new Date(user.lastEmailSentAt).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                    : '—'}
                </td>
                <td style={styles.td}>
                  {user.lastEmailTemplateName || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

            {previewOpen && (
        <div style={styles.modalBackdrop} onClick={() => setPreviewOpen(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3>Предпросмотр письма</h3>
            <div style={styles.formGroup}>
              <label style={styles.label}>Тема письма</label>
              <input
                style={styles.input}
                value={previewSubject}
                onChange={(e) => setPreviewSubject(e.target.value)}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Текст письма (HTML)</label>
              <textarea
                style={styles.textarea}
                value={previewHtml}
                onChange={(e) => setPreviewHtml(e.target.value)}
              />
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button style={styles.secondaryButton} onClick={() => setPreviewOpen(false)}>Закрыть</button>
              <button
                style={styles.button}
                onClick={() => handleSend(previewSubject, previewHtml)}
                disabled={isSending}
              >
                {isSending ? 'Отправка...' : 'Отправить с этим текстом'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showTemplateForm && (
        <div style={styles.modalBackdrop} onClick={() => setShowTemplateForm(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3>{editingTemplate ? 'Редактировать шаблон' : 'Новый шаблон рассылки'}</h3>
            <div style={styles.formGroup}>
              <label style={styles.label}>Название</label>
              <input
                style={styles.input}
                value={templateForm.name}
                onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Тема</label>
              <input
                style={styles.input}
                value={templateForm.subject}
                onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>HTML-текст</label>
              <textarea
                style={styles.textarea}
                value={templateForm.html}
                onChange={(e) => setTemplateForm({ ...templateForm, html: e.target.value })}
                placeholder="Используйте {{name}} для имени, {{email}} для email"
              />
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button style={styles.secondaryButton} onClick={() => setShowTemplateForm(false)}>Отмена</button>
              <button style={styles.button} onClick={saveTemplate}>Сохранить</button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div style={styles.modalBackdrop} onClick={() => setShowDeleteConfirm(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3>Удалить шаблон?</h3>
            <p>Вы уверены, что хотите удалить шаблон <strong>{deleteTemplateName}</strong>?</p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button style={styles.secondaryButton} onClick={() => setShowDeleteConfirm(false)}>Отмена</button>
              <button style={styles.dangerButton} onClick={deleteTemplate}>Удалить</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InactiveUsersEmail;
